// src/pages/AdoptDetailPage.tsx
// おむかえ詳細ページ - 特定のキャラクターをおむかえする

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  HomeIcon,
  UserCircleIcon,
  ShoppingCartIcon,
  HeartIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { useAuth } from '../contexts/AuthContext';
import { CharacterForSale, formatPrice, getAdoptionHistory, AdoptionHistoryItem } from '../services/adoptionService';
import { config } from '../config';
import UserAvatar from '../components/common/UserAvatar';
import XorPixViewer from '../components/xorpix/XorPixViewer';

const AdoptDetailPage: React.FC = () => {
  const { t } = useTranslation('adopt');
  const { characterId } = useParams<{ characterId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [character, setCharacter] = useState<CharacterForSale | null>(null);
  const [adoptionHistory, setAdoptionHistory] = useState<AdoptionHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);

  // Fetch character details
  useEffect(() => {
    const fetchCharacter = async () => {
      if (!characterId) return;

      setIsLoading(true);
      setError(null);

      try {
        // Fetch character details from public API
        const response = await fetch(
          `${config.apiBaseUrl}/api/orifoli/public/characters/${characterId}`
        );

        if (!response.ok) {
          if (response.status === 404) {
            setError(t('errors.notFound'));
          } else {
            throw new Error('Failed to fetch character');
          }
          return;
        }

        const data = await response.json();

        // Check if character is for sale
        if (!data.forSale) {
          setError(t('errors.notForSale'));
          return;
        }

        setCharacter({
          id: data.id,
          name: data.name,
          thumbnailUrl: data.thumbnailUrl,
          bio: data.bio,
          salePrice: data.salePrice || 0,
          noise1Url: data.noise1Url,
          noise2Url: data.noise2Url,
          rid: data.rid,
          imageWidth: data.imageWidth,
          imageHeight: data.imageHeight,
          fallbackNoise1Url: data.fallbackNoise1Url,
          fallbackNoise2Url: data.fallbackNoise2Url,
          cardTheme: data.cardTheme,
          creatorId: data.creatorId,
          creatorName: data.creatorName,
          creatorUsername: data.creatorUsername,
          creatorAvatar: data.creatorAvatar,
          ownerId: data.ownerId,
          ownerName: data.ownerName,
          ownerUsername: data.ownerUsername,
          ownerAvatar: data.ownerAvatar,
          tags: data.tags || [],
          createdAt: data.createdAt,
        });

        // Fetch adoption history
        try {
          const historyResponse = await getAdoptionHistory(characterId, 10);
          setAdoptionHistory(historyResponse.history);
        } catch (historyError) {
          console.error('Failed to fetch adoption history:', historyError);
        }
      } catch (err) {
        console.error('Failed to fetch character:', err);
        setError(t('errors.fetchFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchCharacter();
  }, [characterId]);

  // Handle purchase
  const handlePurchase = async () => {
    if (!user) {
      navigate('/login', { state: { from: `/adopt/${characterId}` } });
      return;
    }

    if (!character) return;

    // Check if user is the owner
    if (user.uid === character.ownerId) {
      alert(t('errors.cannotAdoptOwn'));
      return;
    }

    setIsPurchasing(true);

    try {
      // TODO: Implement checkout session creation
      // const response = await createAdoptionCheckout(characterId);
      // window.location.href = response.checkoutUrl;

      alert(t('errors.comingSoon'));
    } catch (err) {
      console.error('Failed to start checkout:', err);
      alert(t('errors.generic'));
    } finally {
      setIsPurchasing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f8f7f8] dark:bg-gray-900 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !character) {
    return (
      <div className="min-h-screen bg-[#f8f7f8] dark:bg-gray-900">
        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            {t('detail.back')}
          </button>

          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 text-center">
            <ExclamationTriangleIcon className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {error || t('errors.notFound')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              {t('detail.errorDescription')}
            </p>
            <Link
              to="/adopt"
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors"
            >
              <HomeIcon className="w-5 h-5" />
              {t('detail.goToMarket')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isOwner = user?.uid === character.ownerId;

  return (
    <div className="min-h-screen bg-[#f8f7f8] dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          {t('detail.back')}
        </button>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: Character Image */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-sm">
            {character.noise1Url && character.noise2Url && character.rid ? (
              <div className="aspect-square">
                <XorPixViewer
                  characterId={character.id}
                  noise1Url={character.noise1Url}
                  noise2Url={character.noise2Url}
                  rid={character.rid}
                  fallbackNoise1Url={character.fallbackNoise1Url}
                  fallbackNoise2Url={character.fallbackNoise2Url}
                  width={character.imageWidth || 512}
                  height={character.imageHeight || 512}
                  className="w-full h-full object-contain"
                />
              </div>
            ) : character.thumbnailUrl ? (
              <img
                src={character.thumbnailUrl}
                alt={character.name}
                className="w-full aspect-square object-cover"
              />
            ) : (
              <div className="w-full aspect-square bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <UserCircleIcon className="w-24 h-24 text-gray-300 dark:text-gray-600" />
              </div>
            )}
          </div>

          {/* Right: Character Info & Purchase */}
          <div className="space-y-4">
            {/* Character Name & Price */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {character.name}
              </h1>

              {/* Tags */}
              {character.tags && character.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {character.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Bio */}
              {character.bio && (
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 whitespace-pre-wrap">
                  {character.bio}
                </p>
              )}

              {/* Price */}
              <div className="flex items-center justify-between py-4 border-t border-gray-100 dark:border-gray-700">
                <span className="text-gray-500 dark:text-gray-400">{t('detail.price')}</span>
                <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {formatPrice(character.salePrice)}
                </span>
              </div>

              {/* Purchase Button */}
              {isOwner ? (
                <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-xl text-center">
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    {t('detail.isYourListing')}
                  </p>
                  <Link
                    to={`/character/${character.id}`}
                    className="text-purple-500 hover:text-purple-600 text-sm font-medium"
                  >
                    {t('detail.viewCharacterPage')}
                  </Link>
                </div>
              ) : (
                <button
                  onClick={handlePurchase}
                  disabled={isPurchasing}
                  className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isPurchasing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('detail.processing')}
                    </>
                  ) : (
                    <>
                      <ShoppingCartIcon className="w-5 h-5" />
                      {t('detail.adoptButton')}
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Creator & Owner Info */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm space-y-3">
              {/* Creator */}
              <div className="flex items-center gap-3">
                <UserAvatar
                  src={character.creatorAvatar}
                  alt={character.creatorName || ''}
                  size="sm"
                  fallbackInitial={character.creatorName?.charAt(0)}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('creator')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {character.creatorName || 'Unknown'}
                  </p>
                  {character.creatorUsername && (
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      @{character.creatorUsername}
                    </p>
                  )}
                </div>
              </div>

              {/* Owner (if different from creator) */}
              {character.creatorId !== character.ownerId && (
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <UserAvatar
                    src={character.ownerAvatar}
                    alt={character.ownerName || ''}
                    size="sm"
                    fallbackInitial={character.ownerName?.charAt(0)}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('seller')}</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {character.ownerName || 'Unknown'}
                    </p>
                    {character.ownerUsername && (
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        @{character.ownerUsername}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Adoption History */}
            {adoptionHistory.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <HeartIconSolid className="w-4 h-4 text-pink-500" />
                  {t('detail.history')}
                </h3>
                <div className="space-y-2">
                  {adoptionHistory.map((item, index) => (
                    <div
                      key={item.id || index}
                      className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"
                    >
                      <HeartIcon className="w-3 h-3 text-pink-400" />
                      <span>
                        {item.fromOwnerName || t('detail.previousOwner')} → {item.toOwnerName || t('detail.newOwner')}
                      </span>
                      <span className="text-gray-400">
                        ({formatPrice(item.price)})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdoptDetailPage;
